// What? A React component for the search interface.
// Why? To provide a search interface for finding and viewing company information.
// How? The component's logic flow:
// 1. Initialize: Set up state for search results and filters
// 2. API Interaction: Use useAuthFetch for authenticated calls to backend
// 3. User Input: Handle search queries and filter changes
// 4. UI Management: Render search results in a table format
// 5. Navigation: Use useNavigate for viewing company profiles
// 6. Pagination: Handle result pagination

import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { SearchResults } from "./SearchResults";
import type { FilterInterface } from "./SearchFilters";
import { decodeFilterDoc, encodeFilterDoc } from "./filterHydration";
import {
  buildFilterDoc,
  filterValuesFromDoc,
  type FilterValues,
} from "./filterState";
import { ActiveFilters } from "./ActiveFilters";
import { FilterRailLayout, RAIL_PAGE_SX } from "./FilterRail";
import { COMPANY_PRIMARY_FACETS } from "./filterFacets";

import {
  Button,
  Box,
  Typography,
  Stack,
  Alert,
  Card,
  CardContent,
  LinearProgress,
} from "@mui/joy";
import BusinessIcon from "@mui/icons-material/Business";
import EventIcon from "@mui/icons-material/Event";
import QueueIcon from "@mui/icons-material/Queue";
import AddCompanyModal from "../components/Modals/AddCompany/AddCompanyModal";
import AddEventModal from "../components/Modals/AddEvent/AddEventModal";
import ViewQueueModal from "../components/Modals/ViewQueue/ViewQueueModal";
import Loading from "../../../components/Loading";
import PageHeader from "../../../components/PageHeader";
import Page from "../../../components/Pages/Page";
import { FloatingChatWidget } from "../components/BKGAgent";
import ExportDropdown from "../components/ExportDropdown";
import { useFeatureFlag } from "../../../providers/FeatureFlagProvider";
import { useTutorialTarget, useTutorial } from "../../../components/Tutorial";
import { buildBkgCompaniesTour } from "../tutorial";

import {
  useBKGCompanyExport,
  useBKGCompanyPersonsBulk,
  useBKGIngest,
} from "../hooks";
import {
  useCompanyFilterSchemaQuery,
  useCompanySearchQuery,
} from "../hooks/searchQueries";
import {
  convertToCSV,
  downloadCSV,
  extractContacts,
} from "../utils/exportUtils";
import {
  formatHeadquartersAddress,
  formatLatestFunding,
  getDevelopmentStage,
  getLeadPipelineAsset,
  getScalarFieldValue,
  getTherapeuticAreas,
} from "../utils/autodbValues";

// Helper function to compare if two objects are equal or not
const deepEqual = (a: any, b: any): boolean => {
  if (a === b) return true;

  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a === null ||
    b === null
  )
    return false;

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (let key of keysA) {
    if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false;
  }

  return true;
};

type Node = {
  path: string;
  type?: string;
  allowed_values?: any[];
  min?: number | string;
  max?: number | string;
  children?: Node[];
};

function findAllowedValuesByPath(
  obj: Node | undefined,
  targetPath: string,
): any[] {
  // Return empty array if obj is undefined or null
  if (!obj) {
    return [];
  }

  if (obj.path === targetPath && Array.isArray(obj.allowed_values)) {
    return obj.allowed_values;
  }

  if (obj.children && Array.isArray(obj.children)) {
    for (const child of obj.children) {
      const result = findAllowedValuesByPath(child, targetPath);
      if (result.length > 0) {
        return result;
      }
    }
  }

  return [];
}

const hasAttendancePath = (obj: any): boolean => {
  if (!obj || typeof obj !== "object") return false;

  if (
    typeof obj.path === "string" &&
    obj.path.startsWith("value.attendance.")
  ) {
    return true;
  }

  if (Array.isArray(obj.children)) {
    return obj.children.some((child: any) => hasAttendancePath(child));
  }

  return false;
};

const NO_RESULTS: any[] = [];

const Search: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { workspace, isAdmin, isEditor } = useWorkspaceContext();
  const { workspace_id } = useParams() as { workspace_id: string };

  const [error, setError] = useState<string | null>(null);

  const itemsPerPage = 50;
  const urlFilterEncoded = searchParams.get("f") || "";
  const initialFilterDoc = useMemo(
    () => decodeFilterDoc(urlFilterEncoded) || {},
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only seed on first mount
    [],
  );
  const [queryParams, setQueryParams] = useState<any | undefined>({
    skip: 0,
    limit: itemsPerPage,
    company_filter: initialFilterDoc,
  });

  const [filterValues, setFilterValues] = useState<FilterValues>(() =>
    filterValuesFromDoc(initialFilterDoc),
  );

  const lastOwnEncodedRef = useRef<string>(urlFilterEncoded);
  const [filterMountKey, setFilterMountKey] = useState<number>(0);

  useEffect(() => {
    if (urlFilterEncoded !== lastOwnEncodedRef.current) {
      lastOwnEncodedRef.current = urlFilterEncoded;
      const doc = decodeFilterDoc(urlFilterEncoded) || {};
      setQueryParams((prev: any) => ({
        ...(prev || { skip: 0, limit: itemsPerPage }),
        skip: 0,
        company_filter: doc,
      }));
      setFilterValues(filterValuesFromDoc(doc));
      setFilterMountKey((k) => k + 1);
    }
  }, [urlFilterEncoded]);

  // Both start on mount: the initial filter comes from ?f=, not the schema.
  const schemaQuery = useCompanyFilterSchemaQuery(workspace_id, !!workspace);
  const filterParams: FilterInterface | undefined = schemaQuery.data;
  const searchQuery = useCompanySearchQuery(
    workspace_id,
    queryParams,
    !!workspace,
  );
  // During a refetch the previous page stays on screen, so pagination and
  // columns read from the query that page answers, not from queryParams.
  const shownQuery = searchQuery.data?.query ?? queryParams;
  const page = searchQuery.data?.page;
  const results = page?.results ?? NO_RESULTS;
  const totalResults = page?.total ?? 0;
  const prevPage = page?.prev_page;
  const nextPage = page?.next_page;
  const resultsLoading = searchQuery.isFetching;
  const resultsLoaded = !searchQuery.isPending;

  const [addCompanyModalOpen, setAddCompanyModalOpen] =
    useState<boolean>(false);
  const [addEventModalOpen, setAddEventModalOpen] = useState<boolean>(false);
  const [viewQueueModalOpen, setViewQueueModalOpen] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const BKGCompanyExportAPI = useBKGCompanyExport();
  const BKGCompanyPersonsBulkAPI = useBKGCompanyPersonsBulk();
  const BKGIngestAPI = useBKGIngest();

  // Refs for the results tutorial sentinel (heading → first row span).
  const localHeadingRef = useRef<HTMLDivElement>(null);
  const firstRowRef = useRef<HTMLTableRowElement>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // ─── Tutorial wiring ───────────────────────────────────────────────────────
  const agentEnabled = useFeatureFlag("bkg_agent");
  const filtersTutorialRef = useTutorialTarget("bkg-search-filters");
  const resultsTutorialRef = useTutorialTarget("bkg-search-results");
  const addCompanyTutorialRef = useTutorialTarget("bkg-add-company");
  const viewQueueTutorialRef = useTutorialTarget("bkg-view-queue");
  const { startTour } = useTutorial();
  // Stable composed ref so React doesn't re-invoke it every render (an unstable
  // inline callback triggers setRegistry on each call, causing an infinite loop).
  const composedSentinelRef = useCallback(
    (el: HTMLDivElement | null) => {
      sentinelRef.current = el;
      resultsTutorialRef(el);
    },
    [resultsTutorialRef],
  );

  // Read tour conditions through a ref so they're available at fire time without
  // being effect dependencies — otherwise a late flag flip (or the `loading`
  // toggles during result fetches) would re-run the effect, cancel the pending
  // timeout, and the "already started" guard would block rescheduling.
  const tourOptionsRef = useRef({
    agentEnabled,
    canAddData: isEditor,
    canViewQueue: isAdmin,
  });
  tourOptionsRef.current = {
    agentEnabled,
    canAddData: isEditor,
    canViewQueue: isAdmin,
  };

  // Auto-start the guided tour once, after the filters have loaded and layout
  // has settled. Gated only on filterParams (stable once set). startTour() is a
  // no-op if the tour was already dismissed.
  const tourStartedRef = useRef(false);
  useEffect(() => {
    if (tourStartedRef.current || !filterParams) return;
    tourStartedRef.current = true;
    const t = setTimeout(
      () => startTour(buildBkgCompaniesTour(tourOptionsRef.current)),
      800,
    );
    return () => clearTimeout(t);
  }, [filterParams, startTour]);

  // Replay requested from the sidebar "?" control. The sidebar clears dismissals
  // (RESET_DISMISSED) before dispatching, so START_TOUR isn't blocked. When the
  // user triggers replay from another BKG page, the sidebar navigates here and
  // the auto-start effect above handles it on the fresh mount instead.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ module?: string }>).detail;
      if (detail?.module !== "bkg") return;
      tourStartedRef.current = true;
      startTour(buildBkgCompaniesTour(tourOptionsRef.current));
    };
    window.addEventListener("mm-replay-tour", handler);
    return () => window.removeEventListener("mm-replay-tour", handler);
  }, [startTour]);

  // Size the sentinel div to span from the "Search Results" heading to the
  // bottom of the first result row (or just the heading when results are empty).
  // The sentinel is the registered tutorial target so the overlay spotlight
  // covers both the heading and the first concrete result.
  useLayoutEffect(() => {
    const sentinel = sentinelRef.current;
    const heading = localHeadingRef.current;
    if (!sentinel || !heading) return;

    const parentRect = sentinel.parentElement!.getBoundingClientRect();
    const headingRect = heading.getBoundingClientRect();
    const firstRow = firstRowRef.current;
    const bottom = firstRow
      ? firstRow.getBoundingClientRect().bottom
      : headingRect.bottom;

    sentinel.style.top = `${headingRect.top - parentRect.top}px`;
    sentinel.style.height = `${bottom - headingRect.top}px`;
  }, [results]);

  const onFilterChange = useCallback(
    (nextValues: FilterValues) => {
      setFilterValues(nextValues);
      const doc =
        (filterParams && buildFilterDoc(filterParams, nextValues)) || {};
      const encoded = encodeFilterDoc(doc);
      lastOwnEncodedRef.current = encoded;
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (encoded) next.set("f", encoded);
          else next.delete("f");
          return next;
        },
        { replace: true },
      );

      setQueryParams((prev: typeof queryParams) => {
        if (!deepEqual(doc, prev?.company_filter)) {
          return { ...prev, skip: 0, company_filter: doc };
        }
        return prev;
      });
    },
    [filterParams, setSearchParams],
  );

  const onChangePage = async (newPage: number) => {
    await setQueryParams({ ...queryParams, skip: newPage });
  };

  const handleExportCompanies = async () => {
    if (!workspace_id) return;

    setIsExporting(true);
    setError(null);
    try {
      const filterDoc = queryParams.company_filter || {};
      const response = await BKGCompanyExportAPI(workspace_id, {
        company_filter: filterDoc,
      });

      const data = [];
      for (const company of response.results) {
        const lead_asset = getLeadPipelineAsset(company);
        const row: any = {
          "Company Name": getScalarFieldValue(company?.value?.name),
          Location: formatHeadquartersAddress(company),
          "Therapeutic Areas": getTherapeuticAreas(lead_asset).join(", "),
          "Development Stage": getDevelopmentStage(lead_asset),
          "Employee Count": getScalarFieldValue(
            company?.value?.number_of_employees,
          ),
          "Last Funding": formatLatestFunding(
            company?.value?.latest_funding?.value ??
              company?.value?.latest_funding,
          ),
        };

        data.push(row);
      }

      const csv = convertToCSV(data);
      if (!csv) return;

      downloadCSV(csv, "companies_list.csv");
    } catch (err) {
      console.error("Failed to export company data:", err);
      setError("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportContacts = async () => {
    if (!workspace_id) return;

    setIsExporting(true);
    setError(null);
    try {
      const filterDoc = queryParams.company_filter || {};
      const response = await BKGCompanyExportAPI(workspace_id, {
        company_filter: filterDoc,
      });

      const normalizePersonRef = (ref?: string): string | undefined => {
        if (!ref) return undefined;
        return ref.includes("/")
          ? ref
          : `${workspace_id}/company_person/${ref}`;
      };

      const personRefs = new Set<string>();
      for (const company of response.results || []) {
        const leadership =
          company?.value?.leadership?.value?.people?.value ||
          company?.value?.leadership?.value?.people ||
          [];
        for (const leader of leadership) {
          const ref = normalizePersonRef(leader?.reference || leader?._id);
          if (ref) personRefs.add(ref);
        }

        const attendance =
          company?.value?.attendance?.value || company?.value?.attendance || [];
        for (const entry of attendance) {
          const ref = normalizePersonRef(
            entry?.value?.person?.reference ||
              entry?.value?.person?._id ||
              entry?.person?.reference ||
              entry?.person?._id,
          );
          if (ref) personRefs.add(ref);
        }
      }

      const personByRef = new Map<string, any>();
      if (personRefs.size > 0) {
        const personResponse = await BKGCompanyPersonsBulkAPI(
          workspace_id,
          Array.from(personRefs),
        );
        for (const person of personResponse.results || []) {
          if (person?._id) {
            personByRef.set(person._id, person);
          }
        }
      }

      const toScalarString = (input: any): string => {
        if (input === null || input === undefined) return "";
        if (
          typeof input === "string" ||
          typeof input === "number" ||
          typeof input === "boolean"
        ) {
          return String(input).trim();
        }

        if (Array.isArray(input)) {
          for (const item of input) {
            const value = toScalarString(item);
            if (value) return value;
          }
          return "";
        }

        if (typeof input === "object") {
          if ("value" in input) {
            const value = toScalarString((input as { value?: any }).value);
            if (value) return value;
          }

          for (const key of [
            "email",
            "role",
            "title",
            "name",
            "label",
            "text",
          ]) {
            const value = toScalarString((input as Record<string, any>)[key]);
            if (value) return value;
          }
        }

        return "";
      };

      const getName = (person: any) =>
        toScalarString(person?.value?.name ?? person?.name);
      const getTitle = (person: any) =>
        toScalarString(person?.value?.role ?? person?.role);
      const getEmail = (person: any) =>
        toScalarString(person?.value?.email ?? person?.email);
      const unwrapPerson = (entry: any) =>
        entry?.value?.person?.value ||
        entry?.value?.person ||
        entry?.person?.value ||
        entry?.person ||
        personByRef.get(
          normalizePersonRef(
            entry?.value?.person?.reference ||
              entry?.value?.person?._id ||
              entry?.person?.reference ||
              entry?.person?._id ||
              entry?.reference ||
              entry?._id,
          ) || "",
        ) ||
        entry;

      const rows = extractContacts(response.results || [], {
        entityNameColumn: "Company Name",
        contactNameColumn: "Contact Name",
        contactTitleColumn: "Contact Title",
        contactEmailColumn: "Contact Email",
        getEntityName: (company: any) => company?.value?.name?.value,
        getEntityId: (company: any) => company?._id,
        getContactName: getName,
        getContactTitle: getTitle,
        getContactEmail: getEmail,
        sources: [
          {
            label: "Leadership",
            getContacts: (company: any) =>
              company?.value?.leadership?.value?.people?.value ||
              company?.value?.leadership?.value?.people ||
              [],
            getPerson: unwrapPerson,
          },
          {
            label: "Conference",
            getContacts: (company: any) =>
              company?.value?.attendance?.value ||
              company?.value?.attendance ||
              [],
            getPerson: unwrapPerson,
          },
        ],
      });

      const csv = convertToCSV(rows);
      if (!csv) return;

      downloadCSV(csv, "company_contacts.csv");
    } catch (err) {
      console.error("Failed to export company contacts:", err);
      setError("Export failed. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Builds the profile URL for a given companyId — mirrors handleCompanyClick's target so
  // real anchors can be rendered, enabling Cmd/Ctrl/middle-click to open a new tab.
  const getCompanyHref = (companyId: string): string => {
    const companyDocumentId = companyId.includes("/")
      ? companyId.split("/").pop()
      : companyId;
    return `/workspace/${workspace?.id ?? ""}/bkg/companies/${companyDocumentId}`;
  };

  // Updated handleCompanyClick to navigate with company data
  const handleCompanyClick = (companyId: string) => {
    if (!workspace?.id) return;

    // Strip workspace prefix from companyId (e.g., "workspace-id/company/company-id" -> "company-id")
    const companyDocumentId = companyId.includes("/")
      ? companyId.split("/").pop()
      : companyId;

    // Find the company data from results
    const companyData = results.find((company) => company._id === companyId);

    const navigationState: {
      documentId: string;
      companyData?: any;
    } = {
      documentId: companyId,
    };

    if (companyData) {
      navigationState.companyData = companyData.value;
    }

    // Navigate to company detail page carrying the full document id in state
    navigate(
      `/workspace/${workspace?.id ?? ""}/bkg/companies/${companyDocumentId}`,
      {
        state: navigationState,
      },
    );
  };

  const onAddCompany = async (data: Record<string, any>) => {
    return await BKGIngestAPI(workspace_id, data);
  };

  if (!workspace?.id) {
    return (
      <Page>
        <Alert color="danger" variant="soft">
          Workspace not specified.
        </Alert>
      </Page>
    );
  }

  return (
    <Page sx={RAIL_PAGE_SX}>
      <PageHeader>
        <Typography level="h1">Companies</Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          {isEditor && (
            <Button
              ref={addCompanyTutorialRef}
              startDecorator={<BusinessIcon />}
              variant="solid"
              color="primary"
              onClick={() => setAddCompanyModalOpen(true)}
              sx={{
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              Add Company
            </Button>
          )}
          {isEditor && (
            <Button
              startDecorator={<EventIcon />}
              variant="outlined"
              color="primary"
              onClick={() => setAddEventModalOpen(true)}
              sx={{
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              Add Conference
            </Button>
          )}
          <ExportDropdown
            loading={isExporting}
            items={[
              { label: "Export Companies", onClick: handleExportCompanies },
              { label: "Export Contacts", onClick: handleExportContacts },
            ]}
          />
          {isAdmin && (
            <Button
              ref={viewQueueTutorialRef}
              startDecorator={<QueueIcon />}
              variant="outlined"
              color="neutral"
              onClick={() => setViewQueueModalOpen(true)}
              sx={{
                width: { xs: "100%", sm: "auto" },
                justifyContent: { xs: "center", sm: "flex-start" },
              }}
            >
              View Queue
            </Button>
          )}
        </Stack>
      </PageHeader>

      <Stack spacing={3}>
        {schemaQuery.isError && (
          <Alert color="danger" variant="soft">
            Failed to load search filters. Please try again.
          </Alert>
        )}
        {searchQuery.isError && (
          <Alert color="danger" variant="soft">
            Failed to load search results. Please try again.
          </Alert>
        )}
        {error && (
          <Alert color="danger" variant="soft">
            {error}
          </Alert>
        )}

        <FilterRailLayout
          schema={filterParams}
          schemaLoading={schemaQuery.isPending}
          value={filterValues}
          onChange={onFilterChange}
          filterKey={filterMountKey}
          primaryFacets={COMPANY_PRIMARY_FACETS}
          resultCount={totalResults}
          resultsLoading={resultsLoading}
          tutorialRef={filtersTutorialRef}
        >
          {/* Search Results */}
          <Card>
            <CardContent>
              {!resultsLoaded ? (
                <Loading />
              ) : (
                <>
                  {/* Positioned wrapper so the sentinel can be absolutely placed.
                    The sentinel is the registered tutorial target and is sized
                    (via useLayoutEffect) to span heading → first result row. */}
                  <Box sx={{ position: "relative" }}>
                    <Box
                      aria-hidden
                      ref={composedSentinelRef}
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        pointerEvents: "none",
                      }}
                    />
                    <Stack
                      ref={localHeadingRef}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mb: 1.5 }}
                    >
                      <Typography level="h3">Search Results</Typography>
                      <Typography level="body-sm" color="neutral">
                        {totalResults} result{totalResults !== 1 ? "s" : ""}{" "}
                        found
                      </Typography>
                    </Stack>
                    <ActiveFilters
                      schema={filterParams}
                      values={filterValues}
                      onChange={onFilterChange}
                      sx={{ mb: 1.5 }}
                    />
                    {/* Fixed-height slot so the bar appearing doesn't shift the table. */}
                    <Box sx={{ height: 4, mb: 1.5 }}>
                      {resultsLoading && (
                        <LinearProgress
                          thickness={4}
                          aria-label="Updating results"
                        />
                      )}
                    </Box>

                    <Box
                      aria-busy={resultsLoading}
                      sx={{
                        opacity: resultsLoading ? 0.55 : 1,
                        transition: "opacity 150ms ease",
                      }}
                    >
                      <SearchResults
                        results={results}
                        onCompanyClick={handleCompanyClick}
                        hrefForCompany={getCompanyHref}
                        showAttendance={hasAttendancePath(
                          shownQuery?.company_filter,
                        )}
                        firstRowRef={firstRowRef}
                      />
                    </Box>
                  </Box>

                  {/* Pagination */}
                  {totalResults > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 3,
                      }}
                    >
                      <Typography level="body-sm" color="neutral">
                        Showing {Math.min(shownQuery.skip + 1, totalResults)}-
                        {Math.min(shownQuery.skip + itemsPerPage, totalResults)}{" "}
                        of {totalResults} results
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {prevPage !== undefined && (
                          <Button
                            variant="outlined"
                            size="sm"
                            onClick={() => onChangePage(prevPage!)}
                            disabled={prevPage === undefined}
                          >
                            Previous
                          </Button>
                        )}
                        {nextPage !== undefined && (
                          <Button
                            variant="outlined"
                            size="sm"
                            onClick={() => onChangePage(nextPage!)}
                            disabled={nextPage === undefined}
                          >
                            Next
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </FilterRailLayout>
      </Stack>

      <AddCompanyModal
        open={addCompanyModalOpen}
        onClose={() => setAddCompanyModalOpen(false)}
        onAdd={onAddCompany}
      />

      {filterParams && (
        <AddEventModal
          open={addEventModalOpen}
          onClose={() => setAddEventModalOpen(false)}
          onAdd={onAddCompany}
          eventOptions={findAllowedValuesByPath(
            filterParams as Node,
            "value.attendance.value.event_name.value",
          )}
        />
      )}

      <ViewQueueModal
        open={viewQueueModalOpen}
        onClose={() => setViewQueueModalOpen(false)}
        workspaceId={workspace?.id ?? ""}
      />
      <FloatingChatWidget workspaceId={workspace?.id ?? ""} />
    </Page>
  );
};

export default Search;
